/**
 * Plugin Registry System for Vue Web Components
 *
 * This system allows for dynamic loading of plugins based on component attributes
 * and provides extension points for custom functionality.
 */

class PluginRegistry {
  constructor() {
    this.plugins = new Map();
    this.hooks = new Map();
    this.loadedPlugins = new Set();
    this.config = {
      autoLoad: true,
      loadTimeout: 5000,
    };
  }

  /**
   * Register a plugin
   */
  register(name, plugin) {
    if (this.plugins.has(name)) {
      console.warn(`Plugin "${name}" is already registered. Overwriting.`);
    }

    // Validate plugin structure
    if (!this.validatePlugin(plugin)) {
      throw new Error(`Invalid plugin structure for "${name}"`);
    }

    this.plugins.set(name, {
      ...plugin,
      name,
      loaded: false,
      instance: null,
    });

    console.log(`Plugin "${name}" registered successfully`);
  }

  /**
   * Validate plugin structure
   */
  validatePlugin(plugin) {
    const required = ["init", "destroy"];
    return required.every((method) => typeof plugin[method] === "function");
  }

  /**
   * Load a plugin
   */
  async load(name, options = {}) {
    if (this.loadedPlugins.has(name)) {
      console.log(`Plugin "${name}" is already loaded`);
      return this.plugins.get(name).instance;
    }

    const plugin = this.plugins.get(name);
    if (!plugin) {
      throw new Error(`Plugin "${name}" is not registered`);
    }

    try {
      console.log(`Loading plugin "${name}"...`);

      // Initialize plugin with options
      const instance = await plugin.init(options);

      plugin.loaded = true;
      plugin.instance = instance;
      this.loadedPlugins.add(name);

      // Execute post-load hooks
      await this.executeHooks("plugin:loaded", { name, instance, options });

      console.log(`Plugin "${name}" loaded successfully`);
      return instance;
    } catch (error) {
      console.error(`Failed to load plugin "${name}":`, error);
      throw error;
    }
  }

  /**
   * Unload a plugin
   */
  async unload(name) {
    if (!this.loadedPlugins.has(name)) {
      console.log(`Plugin "${name}" is not loaded`);
      return;
    }

    const plugin = this.plugins.get(name);
    if (!plugin) {
      throw new Error(`Plugin "${name}" is not registered`);
    }

    try {
      console.log(`Unloading plugin "${name}"...`);

      // Execute pre-unload hooks
      await this.executeHooks("plugin:unloading", { name, instance: plugin.instance });

      // Destroy plugin instance
      if (plugin.instance && typeof plugin.destroy === "function") {
        await plugin.destroy(plugin.instance);
      }

      plugin.loaded = false;
      plugin.instance = null;
      this.loadedPlugins.delete(name);

      console.log(`Plugin "${name}" unloaded successfully`);
    } catch (error) {
      console.error(`Failed to unload plugin "${name}":`, error);
      throw error;
    }
  }

  /**
   * Load plugins based on conditions
   */
  async loadConditional(conditions = {}) {
    const pluginsToLoad = [];

    for (const [name, plugin] of this.plugins) {
      if (this.loadedPlugins.has(name)) continue;

      // Check if plugin should be loaded based on conditions
      if (this.shouldLoadPlugin(plugin, conditions)) {
        pluginsToLoad.push(name);
      }
    }

    // Load plugins in parallel
    const loadPromises = pluginsToLoad.map((name) =>
      this.load(name, conditions).catch((error) => {
        console.error(`Failed to conditionally load plugin "${name}":`, error);
        return null;
      }),
    );

    const results = await Promise.all(loadPromises);
    return results.filter((result) => result !== null);
  }

  /**
   * Check if plugin should be loaded based on conditions
   */
  shouldLoadPlugin(plugin, conditions) {
    if (!plugin.conditions) return false;

    // Check attribute conditions
    if (plugin.conditions.attributes) {
      for (const [attr, value] of Object.entries(plugin.conditions.attributes)) {
        if (conditions.attributes && conditions.attributes[attr] === value) {
          return true;
        }
      }
    }

    // Check feature flags
    if (plugin.conditions.features) {
      for (const feature of plugin.conditions.features) {
        if (conditions.features && conditions.features.includes(feature)) {
          return true;
        }
      }
    }

    // Check environment conditions
    if (plugin.conditions.environment) {
      const env = plugin.conditions.environment;
      if (env.development && __DEV__) return true;
      if (env.production && !__DEV__) return true;
    }

    return false;
  }

  /**
   * Get loaded plugin instance
   */
  getInstance(name) {
    const plugin = this.plugins.get(name);
    return plugin && plugin.loaded ? plugin.instance : null;
  }

  /**
   * Check if plugin is loaded
   */
  isLoaded(name) {
    return this.loadedPlugins.has(name);
  }

  /**
   * Get all loaded plugins
   */
  getLoadedPlugins() {
    return Array.from(this.loadedPlugins);
  }

  /**
   * Register a hook
   */
  addHook(event, callback) {
    if (!this.hooks.has(event)) {
      this.hooks.set(event, []);
    }
    this.hooks.get(event).push(callback);
  }

  /**
   * Remove a hook
   */
  removeHook(event, callback) {
    if (this.hooks.has(event)) {
      const hooks = this.hooks.get(event);
      const index = hooks.indexOf(callback);
      if (index > -1) {
        hooks.splice(index, 1);
      }
    }
  }

  /**
   * Execute hooks for an event
   */
  async executeHooks(event, data = {}) {
    if (!this.hooks.has(event)) return;

    const hooks = this.hooks.get(event);
    const promises = hooks.map((hook) => {
      try {
        return Promise.resolve(hook(data));
      } catch (error) {
        console.error(`Hook execution failed for event "${event}":`, error);
        return Promise.resolve();
      }
    });

    await Promise.all(promises);
  }

  /**
   * Clear all plugins and hooks
   */
  clear() {
    // Unload all plugins
    const unloadPromises = Array.from(this.loadedPlugins).map((name) =>
      this.unload(name).catch((error) => {
        console.error(`Failed to unload plugin "${name}" during clear:`, error);
      }),
    );

    Promise.all(unloadPromises).then(() => {
      this.plugins.clear();
      this.hooks.clear();
      this.loadedPlugins.clear();
      console.log("Plugin registry cleared");
    });
  }

  /**
   * Get registry statistics
   */
  getStats() {
    return {
      totalPlugins: this.plugins.size,
      loadedPlugins: this.loadedPlugins.size,
      registeredHooks: this.hooks.size,
      config: this.config,
    };
  }
}

// Create and export singleton instance
export const pluginRegistry = new PluginRegistry();

// Export class for testing or multiple instances
export { PluginRegistry };
